import { Card, Col, Row, Typography, theme as antdTheme } from 'antd';
import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  BookOutlined,
  DollarOutlined,
  FundOutlined,
  PieChartOutlined,
  ProfileOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  TableOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';
import { FinanceMonthRail } from './finance-month-rail';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const tiles: Tile[] = [
  {
    to: '/finance/accounts',
    title: 'Chart of accounts',
    description: 'Hierarchy of asset / liability / income / expense / equity accounts.',
    icon: <ApartmentOutlined />,
  },
  {
    to: '/finance/bank-accounts',
    title: 'Bank & cash accounts',
    description: 'Operating bank accounts and the single cash account, with running balance.',
    icon: <BankOutlined />,
  },
  {
    to: '/finance/expenses',
    title: 'Expenses',
    description: 'Record an expense — auto-posts DR expense / CR cash·bank to the ledger.',
    icon: <ShoppingCartOutlined />,
  },
  {
    to: '/finance/other-income',
    title: 'Other income',
    description: 'Donations, grants, premises rent, misc. Auto-posts to the ledger.',
    icon: <DollarOutlined />,
  },
  {
    to: '/finance/journals',
    title: 'Journal vouchers',
    description: 'Manual balanced entries for adjustments, opening balances, corrections.',
    icon: <SwapOutlined />,
  },
  {
    to: '/finance/daybook',
    title: 'Daybook',
    description: 'Every ledger entry in a date range. Filter to a single account if needed.',
    icon: <BookOutlined />,
  },
  {
    to: '/finance/trial-balance',
    title: 'Trial balance',
    description: 'Per-account totals as of any date. Sums should net to zero.',
    icon: <TableOutlined />,
  },
  {
    to: '/finance/reconciliation',
    title: 'Bank reconciliation',
    description: 'Tick off ledger entries against the bank statement. Live variance vs statement balance.',
    icon: <AuditOutlined />,
  },
  {
    to: '/finance/income-expenditure',
    title: 'Income & Expenditure',
    description: 'Period earnings vs operating costs. Net surplus / deficit at the bottom.',
    icon: <FundOutlined />,
  },
  {
    to: '/finance/balance-sheet',
    title: 'Balance sheet',
    description: 'Assets, liabilities, equity, and net surplus to date. Variance check at the top.',
    icon: <PieChartOutlined />,
  },
  {
    to: '/finance/bank-book',
    title: 'Cash & Bank book',
    description: 'Chronological view of one cash or bank account with running balance.',
    icon: <ProfileOutlined />,
  },
  {
    to: '/finance/cash-handover',
    title: 'Cash handover',
    description: 'Day-end voucher — opening + receipts − payments = expected. Cashier counts cash and hands over with a printable voucher.',
    icon: <WalletOutlined />,
  },
];

export function FinanceHubPage() {
  const { token } = antdTheme.useToken();
  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module"
        title="Accounts & ledger"
        description="Chart of accounts, ledger entries, financial transactions, and reports. Vendors and the school store land with the inventory module."
      />

      <FinanceMonthRail />

      <Row gutter={[16, 16]}>
        {tiles.map((t) => (
          <Col key={t.to} xs={24} md={12} xl={8}>
            <Link to={t.to} style={{ textDecoration: 'none' }}>
              <Card
                hoverable
                className="lift-card"
                style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', gap: 14 }}>
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: token.colorFillTertiary,
                      color: token.colorPrimary,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        {t.title}
                      </Typography.Text>
                      <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {t.description}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </div>
  );
}
